import { isoDate, stayRange, usDate } from '../dates';
import {
  type Engine,
  MissingSettingError,
  NeedsInputError,
  type Settings,
} from '../types';
import { joinQuery } from '../url';

function home(settings: Settings, engine: string): [string, string] {
  const [lat, lng] = settings.home?.split(',') ?? [];
  if (!lat || !lng) throw new MissingSettingError('home', engine);
  return [lat, lng];
}

const REC_AMENITIES: Array<
  [id: string, token: string, label: string, value: string]
> = [
  ['water', 'water', 'Water hookup', 'water hookup'],
  ['electric', 'electric', 'Electric hookup', 'electricity hookup'],
  ['sewer', 'sewer', 'Sewer hookup', 'sewer hookup'],
  ['fire', 'fire', 'Fire pit', 'fire pit'],
  ['toilet', 'toilet', 'Flush toilet', 'flush toilet'],
  ['waterfront', 'waterfront', 'Waterfront', 'waterfront'],
];

// Site types, amenities and site features describe campsites.
const campingOnly = {
  when: { param: 'inventory', unset: true as const },
  whenLabel: 'only applies to camping',
};

export const recreation: Engine = {
  id: 'recreation',
  name: 'Recreation.gov',
  bang: 'rec',
  aliases: ['recgov'],
  homepage: 'https://www.recreation.gov',
  category: 'Outdoors',
  description:
    'Campsites, cabins, permits and tours on US public lands. Type a place, or use near for your home location.',
  params: [
    {
      id: 'near',
      type: 'flag',
      token: 'near',
      label: 'Near my home location',
      group: 'Location',
      help: 'Uses Home location from Settings. Without it, type a place: !rec yellowstone.',
    },
    {
      id: 'radius',
      type: 'number',
      suffix: 'mi',
      label: 'Within (miles)',
      group: 'Location',
      placeholder: '50',
      help: 'Around your home location: with near, or with no place typed.',
    },
    {
      id: 'inventory',
      type: 'choice',
      label: 'Looking for',
      group: 'Type',
      help: 'Default is camping.',
      options: [
        { value: 'day use', token: 'dayuse', label: 'Day use' },
        { value: 'tickets & tours', token: 'tours', label: 'Tickets & tours' },
        { value: 'permits', token: 'permits', label: 'Permits' },
        { value: 'activity pass', token: 'pass', label: 'Passes' },
      ],
    },
    {
      id: 'site',
      ...campingOnly,
      type: 'choice',
      label: 'Site type',
      group: 'Type',
      options: [
        { value: 'tent', token: 'tent', label: 'Tent' },
        { value: 'rv/ motorhome/ trailer', token: 'rv', label: 'RV / trailer' },
        {
          value: 'lodging (cabins/yurts/lookouts)',
          token: 'cabin',
          label: 'Cabins, yurts, lookouts',
        },
      ],
    },
    {
      id: 'dates',
      when: {
        param: 'inventory',
        in: ['day use', 'tickets & tours', 'permits'],
        orUnset: true,
      },
      whenLabel: 'doesn’t apply to passes',
      type: 'dates',
      label: 'Dates',
      group: 'Dates',
      help: 'With dates, only available sites show.',
    },
    {
      id: 'group',
      type: 'number',
      prefix: 'g',
      label: 'Group size',
      group: 'Dates',
      placeholder: '4',
    },
    {
      id: 'rvLength',
      when: [
        { param: 'inventory', unset: true },
        { param: 'site', in: ['rv/ motorhome/ trailer'], orUnset: true },
      ],
      whenLabel: 'only applies to RV and camping searches',
      type: 'number',
      suffix: 'ft',
      label: 'RV length (feet)',
      group: 'Dates',
      placeholder: '30',
    },
    {
      id: 'pets',
      ...campingOnly,
      type: 'flag',
      token: 'pets',
      label: 'Pet friendly',
      group: 'Site',
    },
    {
      id: 'groupSite',
      ...campingOnly,
      type: 'flag',
      token: 'groupsite',
      label: 'Group site',
      group: 'Site',
    },
    {
      id: 'accessible',
      ...campingOnly,
      type: 'flag',
      token: 'ada',
      label: 'Accessible',
      group: 'Site',
    },
    ...REC_AMENITIES.map(([id, token, label]) => ({
      id,
      type: 'flag' as const,
      token,
      label,
      group: 'Amenities',
      ...campingOnly,
    })),
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'available', token: 'avail', label: 'Most available' },
        { value: 'distance', token: 'closest', label: 'Closest' },
        { value: 'average_rating', token: 'rated', label: 'Highest rated' },
        { value: 'price asc', token: 'low', label: 'Lowest price' },
      ],
    },
  ],
  build({ query, values, settings }) {
    const url = new URL('https://www.recreation.gov/search');
    if (query) url.searchParams.set('q', query);
    // With no query there's nothing to geocode, so fall back to home.
    const atHome = values.near === true || (!query && !!settings.home);
    if (atHome) {
      const [lat, lng] = home(settings, 'Recreation.gov');
      url.searchParams.set('lat', lat);
      url.searchParams.set('lng', lng);
    }
    // The radius is measured from home; with a typed place there's no point.
    if (typeof values.radius === 'number') {
      if (!atHome)
        throw new NeedsInputError(
          'Within (miles) measures from your home location: add .near, or leave out the place.',
          'Recreation.gov'
        );
      url.searchParams.set('radius', String(values.radius));
    }
    url.searchParams.set(
      'inventory_type',
      (values.inventory as string | undefined) ?? 'camping'
    );
    if (typeof values.site === 'string')
      url.searchParams.set('campsite_type', values.site);
    const stay =
      typeof values.dates === 'string' ? stayRange(values.dates) : undefined;
    if (stay) {
      url.searchParams.set('checkin', usDate(stay.start));
      url.searchParams.set('checkout', usDate(stay.end));
    }
    if (typeof values.group === 'number')
      url.searchParams.set('group-size', String(values.group));
    if (typeof values.rvLength === 'number')
      url.searchParams.set('vehicle-length', String(values.rvLength));
    if (values.pets === true)
      url.searchParams.append('key_characteristics', 'pet friendly');
    if (values.groupSite === true)
      url.searchParams.append('key_characteristics', 'group site');
    if (values.accessible === true)
      url.searchParams.append('key_characteristics', 'accessibility');
    for (const [id, , , value] of REC_AMENITIES)
      if (values[id] === true) url.searchParams.append('amenities', value);
    const sort =
      (values.sort as string | undefined) ?? (stay ? 'available' : undefined);
    if (sort) url.searchParams.set('sort', sort);
    // The site expects %20 rather than + for spaces.
    return url.toString().replace(/\+/g, '%20');
  },
};

const HIP_AMENITIES: Array<
  [id: string, token: string, label: string, value: string]
> = [
  ['toilet', 'toilet', 'Toilet', 'toilet'],
  ['fire', 'fire', 'Campfires', 'fire'],
  ['shower', 'shower', 'Showers', 'shower'],
  ['electric', 'electric', 'Electric hookup', 'electricity-hookup'],
  ['water', 'water', 'Water hookup', 'water-hookup'],
];

export const hipcamp: Engine = {
  id: 'hipcamp',
  name: 'Hipcamp',
  bang: 'hip',
  aliases: ['hipcamp'],
  homepage: 'https://www.hipcamp.com',
  category: 'Outdoors',
  description:
    'Private-land campsites, cabins and RV spots around your home location (Hipcamp has no place search by name).',
  params: [
    {
      id: 'stay',
      type: 'choice',
      label: 'Stay type',
      group: 'Type',
      options: [
        { value: 'tents', token: 'tent', label: 'Tent' },
        { value: 'rv', token: 'rv', label: 'RV' },
        { value: 'lodging', token: 'cabin', label: 'Lodging' },
      ],
    },
    { id: 'dates', type: 'dates', label: 'Dates', group: 'Dates' },
    {
      id: 'guests',
      type: 'number',
      prefix: 'g',
      label: 'Adults',
      group: 'Dates',
      placeholder: '2',
    },
    {
      id: 'pets',
      type: 'flag',
      token: 'pets',
      label: 'Pet friendly',
      group: 'Site',
    },
    {
      id: 'instant',
      type: 'flag',
      token: 'instant',
      label: 'Instant book',
      group: 'Site',
    },
    ...HIP_AMENITIES.map(([id, token, label]) => ({
      id,
      type: 'flag' as const,
      token,
      label,
      group: 'Amenities',
    })),
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'price,asc', token: 'low', label: 'Price: low to high' },
        { value: 'price,desc', token: 'high', label: 'Price: high to low' },
        { value: 'distance,asc', token: 'closest', label: 'Closest' },
      ],
    },
  ],
  build({ query, values, settings }) {
    const [lat, lng] = home(settings, 'Hipcamp');
    const url = new URL('https://www.hipcamp.com/en-US/search');
    url.searchParams.set('lat', lat);
    url.searchParams.set('lng', lng);
    // Hipcamp only uses q as a label; keep it so the search box shows it.
    const label = joinQuery(query);
    if (label) url.searchParams.set('q', label);
    const stay =
      typeof values.dates === 'string' ? stayRange(values.dates) : undefined;
    if (stay) {
      url.searchParams.set('arrive', isoDate(stay.start));
      url.searchParams.set('depart', isoDate(stay.end));
    }
    if (typeof values.guests === 'number')
      url.searchParams.set('adults', String(values.guests));
    if (values.pets === true) url.searchParams.set('pets', '1');
    if (values.instant === true) url.searchParams.set('instantBook', 'true');
    if (typeof values.stay === 'string')
      url.searchParams.set('accommodations', values.stay);
    for (const [id, , , value] of HIP_AMENITIES)
      if (values[id] === true) url.searchParams.append('amenities', value);
    if (typeof values.sort === 'string') {
      const [property, order] = values.sort.split(',');
      url.searchParams.set('sortProperty', property ?? '');
      url.searchParams.set('sortOrder', order ?? '');
    }
    return url.toString();
  },
};
